import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bot } from "lucide-react";
import { useApiMutation } from "@/hooks/useApiMutation";
import { useToast } from "@/hooks/use-toast";


interface AIChatToggleTabProps {
  workspaceId: string;
  initialEnabled: boolean;
}

export function AIChatToggleTab({ workspaceId, initialEnabled }: AIChatToggleTabProps) {
  const [enabled, setEnabled] = useState(initialEnabled);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const updateMutation = useApiMutation({
    endpoint: (vars: { workspaceId: string; aiChatEnabled?: boolean }) => `/api/workspaces/${vars.workspaceId}`,
    method: "PATCH",
    invalidate: [["/api/workspaces"]],
    onSuccess: () => {
      toast({
        title: "Settings updated",
        description: `AI Chat has been ${enabled ? "enabled" : "disabled"}.`,
      });
    },
    onError: () => {
      setEnabled(!enabled);
    },
  });

  const handleToggle = (checked: boolean) => {
    setEnabled(checked);
    updateMutation.mutate({ workspaceId, aiChatEnabled: checked });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5" />
          <CardTitle>AI Chat Assistant</CardTitle>
        </div>
        <CardDescription>
          Enable or disable the AI chat assistant for this workspace
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="ai-chat-toggle">Enable AI Chat</Label>
            <p className="text-sm text-muted-foreground">
              Allow workspace members to use the AI assistant
            </p>
          </div>
          <Switch
            id="ai-chat-toggle"
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={updateMutation.isPending}
          />
        </div>
      </CardContent>
    </Card>
  );
}
